/**
 * FarmersPulse — Full feature lists per marketing tool (Tools 13–24).
 *
 * Sourced verbatim from the user's 2026-02 FarmersPulse Digital Farmer
 * Toolkit reference (14 sections, adapted for the 12 marketing tools).
 *
 * Shape and language rules mirror
 * /frontend/src/data/farmerToolkitFeatures.js — plain-farmer language,
 * approval-first framing, no invented certifications or numbers.
 */

export const MARKETING_TOOL_FEATURES = {
  "brand-builder": {
    lead: "A guided setup that packages your farm story into a professional identity you can use everywhere.",
    groups: [
      { label: "Your brand pack includes", items: [
        "Verified farmer and farm profile",
        "Farm story and photographs",
        "Products and services offered",
        "Farm stand and market schedule",
        "Phone, inquiry and WhatsApp buttons",
        "Map and driving directions",
        "Multilingual pages + text-to-speech",
        "Printable QR code for markets and packaging",
        "Reviews and testimonials block",
        "Publish at farmname.farmerspulse.com",
      ]},
    ],
  },

  "product-marketing": {
    lead: "One product record becomes a full listing across every channel that sells for you.",
    groups: [
      { label: "Every product listing captures", items: [
        "Photograph",
        "Product and variety",
        "Price and unit",
        "Retail and wholesale price",
        "Available quantity",
        "Harvest or availability date",
        "Pickup and delivery options",
        "Organic or certification status",
        "Availability chip — Available now · Coming soon · Sold out",
      ]},
      { label: "Fans out to", items: [
        "Farm website",
        "Farm shop",
        "Marketplace listing",
        "Marketing drafts",
        "Buyer-matching feed",
      ]},
    ],
  },

  "social-post-maker": {
    lead: "Real farm facts become social posts you're proud to publish.",
    groups: [
      { label: "Post types drafted from your farm records", items: [
        "Crop-availability announcements",
        "Fresh-today posts",
        "Harvest stories",
        "Farmers-market reminders",
        "Seasonal promotions",
        "Festival and holiday offers",
        "Photo captions and alt text",
        "Website article snippets",
      ]},
      { label: "Every draft carries", items: [
        "Caption + hashtags + suggested photo direction",
        "The exact farm record it pulled from",
        "Approval-required chip — nothing publishes without your tap",
      ]},
    ],
  },

  "email-whatsapp-campaigns": {
    lead: "Reach your customers directly on the channels they actually read.",
    groups: [
      { label: "Message types drafted", items: [
        "Weekly farm newsletters",
        "Crop-availability announcements",
        "Farmers-market reminders",
        "Preorder invitations",
        "Abandoned-cart recovery",
        "Customer win-back messages",
        "Welcome messages for new customers",
        "Google Business Profile posts",
      ]},
      { label: "Every send captures", items: [
        "Subject line, email body and short-form (SMS/WhatsApp) version",
        "Translated versions when your customer base needs them",
        "Best send-window recommendation drawn from past opens",
        "Approval-required chip — nothing sends without your tap",
      ]},
    ],
  },

  "deals-promotions": {
    lead: "Move product before it wilts — with offers priced above your break-even.",
    groups: [
      { label: "Offer types generated", items: [
        "Discount and markdown",
        "Bundle",
        "Coupon",
        "Flash sale",
        "Surplus offer",
        "Seasonal promotion",
        "Festival and holiday offer",
        "Referral credit",
      ]},
      { label: "Every offer package includes", items: [
        "Copy for farm-page banner, WhatsApp and QR flyer",
        "Break-even guardrail — no price ever loses you money",
        "Expiry timing and stock cap",
        "Optional FoodPulse donation fallback if the batch doesn't clear",
      ]},
    ],
  },

  "seasonal-calendar": {
    lead: "Plan the season once. The assistant reminds you the day before each move.",
    groups: [
      { label: "Horizons available", items: [
        "7-day view — this week's moves, day-by-day",
        "30-day view — aligned to harvests and market days",
        "365-day view — annual promotional calendar",
      ]},
      { label: "Plan items auto-drafted", items: [
        "Product-availability announcements",
        "Farmers-market reminders",
        "CSA renewal campaigns",
        "Wholesale-sheet refreshes",
        "Seasonal and festival promotions",
        "Review-request cycles",
        "Weekly newsletter cadence",
      ]},
    ],
  },

  "local-seo": {
    lead: "Get found by the neighbors who don't know you exist yet.",
    groups: [
      { label: "Pages and profiles generated", items: [
        "Farm home page with local keywords",
        "Product-availability pages",
        "Search-friendly crop pages",
        "Google Business Profile update",
        "Three FAQ prompts your neighbors actually search for",
      ]},
      { label: "Multilingual coverage", items: [
        "Publish in 37 languages",
        "Text-to-speech on every page",
        "Multi-language crop and product names indexed for search",
      ]},
    ],
  },

  "reviews-loyalty": {
    lead: "Turn one satisfied customer into three.",
    groups: [
      { label: "Reviews and reputation", items: [
        "Verified customer reviews tied to actual transactions",
        "Post-purchase review requests drafted for approval",
        "Response-rate and fulfillment metrics",
        "Complaint and resolution workflow",
        "Farmer badges and Social Capital integration",
      ]},
      { label: "Repeat business", items: [
        "Referral offers for regulars",
        "Loyalty triggers on 3, 5 and 10 orders",
        "Dormant-customer win-back campaigns",
        "Birthday, festival and seasonal notes",
      ]},
    ],
  },

  "csa-farm-box": {
    lead: "Fill every box, every week — from enrollment to renewal.",
    groups: [
      { label: "CSA setup and season", items: [
        "Season dates and membership capacity",
        "Box sizes, prices and add-ons",
        "Landing-page copy for enrollment",
        "Member agreement",
        "Payment structure and installments",
      ]},
      { label: "Weekly member communications", items: [
        "'What's in the box' preview post",
        "Storage, shelf life and use-first notes",
        "Recipes, preparation and preservation tips",
        "Missed-pickup drafts",
        "Farmer's message or video",
        "Renewal and referral campaigns",
      ]},
      { label: "Personalised farm-box page per member", items: [
        "Box contents and variety identification",
        "Nutrition and allergy notices",
        "QR code on the box opens the week's live page",
      ]},
    ],
  },

  "market-promoter": {
    lead: "Draw the crowd before you drive there.",
    groups: [
      { label: "Market-day package (drafted the morning of)", items: [
        "'We're here today' social post",
        "WhatsApp blast to regulars 90 minutes before you open",
        "Product list pulled from your inventory",
        "Farm-stand-only offers",
        "QR flyer copy for the market table",
        "Customer reminder for last week's buyers",
      ]},
      { label: "After the market", items: [
        "Review-request drafts for stallholders you spoke with",
        "Follow-up notes to new customers you met",
        "Sales summary sent to your Marketing Coach dashboard",
      ]},
    ],
  },

  "wholesale-buyer-marketing": {
    lead: "Professional buyer outreach without writing a single sales email.",
    groups: [
      { label: "Materials generated", items: [
        "Wholesale availability sheet pulled from inventory",
        "Buyer pitch tuned to the buyer type",
        "Restaurant or institution introduction message",
        "Request-for-quotation template",
        "Bulk and wholesale pricing sheet",
        "Delivery-zone and lead-time notes",
      ]},
      { label: "Follow-up automation", items: [
        "Soft follow-up drafts if a buyer goes quiet",
        "Combined-supply proposals when multiple farms meet demand together",
        "Delivery plan and payment allocation for combined proposals",
      ]},
    ],
  },

  "marketing-coach": {
    lead: "Plain-language answers to the questions farmers actually ask.",
    groups: [
      { label: "Weekly Marketing Health Check", items: [
        "Is the farm discoverable in local search?",
        "Are products and prices current?",
        "Can customers order easily?",
        "Are inquiries answered quickly?",
        "Are abandoned carts being recovered?",
        "Are past customers encouraged to return?",
        "Are reviews being collected?",
        "Which products get interest but few sales?",
      ]},
      { label: "Questions the dashboard answers", items: [
        "How many people found my farm?",
        "Which products attracted attention?",
        "How many inquiries became orders?",
        "Which customers are likely to reorder?",
        "Which pickup location performs best?",
        "Which campaign produced sales?",
        "What remained unsold?",
        "Which crop produced the greatest return?",
        "What should I promote this week?",
      ]},
      { label: "Every week ends with", items: [
        "A prioritised 30-day plan — no overwhelming to-do list",
        "One specific move for the coming week",
      ]},
    ],
  },
};
